import React, { useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import useEditor from 'packages/hooks/useEditor';

interface Props {
    id?: string;
}

const EditorCanvas = React.forwardRef<any, Props>((props, ref) => {
    const { loading, initManipulationRenderer, manipulationRenderer, creativeType } = useEditor();
    const canvasContainerRef = useRef<HTMLDivElement>(null);

    const setDimensions = useMemo(() => {
        const callback = (newHeight: number) => {
            if (!canvasContainerRef.current) {
                return;
            }

            canvasContainerRef.current.style.height = `${newHeight}px`;

            if (!manipulationRenderer) {
                return;
            }

            manipulationRenderer.calculateScaleRatio();
        };

        let idleCallbackId = 0;
        let latestHeight: number;

        return (h: number) => {
            latestHeight = h;

            if (!idleCallbackId) {
                idleCallbackId = window.requestIdleCallback(
                    () => {
                        idleCallbackId = 0;
                        callback(latestHeight);
                    },
                    { timeout: 10 },
                );
            }
        };
    }, [manipulationRenderer]);

    useImperativeHandle(ref, () => ({
        setDimensions,
    }));

    useEffect(() => {
        if (!manipulationRenderer) {
            return;
        }

        const scale = manipulationRenderer.calculateScaleRatio();
        manipulationRenderer.setScale(scale);
        manipulationRenderer.redraw();
    }, [manipulationRenderer]);

    useEffect(() => {
        if (loading) {
            return;
        }

        initManipulationRenderer(canvasContainerRef.current, 0);
    }, [loading]);

    useEffect(() => {
        if (!manipulationRenderer || creativeType === 'IMAGE') {
            return;
        }

        return () => (manipulationRenderer as any).pausePlayback();
    }, [manipulationRenderer]);

    return (
        <div
            className="canvas-container d-flex align-items-center justify-content-center"
            id={props.id}
            ref={canvasContainerRef}
        />
    );
});

export default EditorCanvas;
